<script lang="ts">
  import Select from "@/components/Select.svelte";
  import AdminShell from "@/components/AdminShell.svelte";
  import {
    api,
    ApiError,
    type AdminServer,
    type AdminUser,
    type AdminUserDetails,
    type AdminUserLedgerItem,
  } from "@/api";
  import { token, user } from "@/stores/session";
  import { confirmModal, promptModal } from "@/stores/modal";
  import { route, routeHref } from "@/router";
  import { statusLabels, ledgerSourceLabels } from "@/config";
  import { copyToClipboard } from "@/utils/clipboard";
  import { formatDate, formatDateTime, formatNumber } from "@/utils/format";
  import AdminTable from "@/components/AdminTable.svelte";
  import EmptyState from "@/components/EmptyState.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import Button from "@/components/Button.svelte";

  let nextCursor = $state<string | null>(null);
  let pageCursors = $state<Array<string | null>>([null]);
  let users: AdminUser[] = $state([]);
  let query = $state("");
  let debouncedQuery = $state("");
  let page = $state(1);
  let loading = $state(true);
  let busy = $state(false);
  let error = $state<string | null>(null);

  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let usersAbort: AbortController | null = null;

  $effect(() => {
    const term = query;
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    searchTimer = setTimeout(() => {
      debouncedQuery = term;
    }, 300);
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  });

  // Card view state
  let selectedUser: AdminUserDetails | null = $state(null);
  let userLoading = $state(false);
  let copiedField = $state<string | null>(null);

  // Card view ledger state
  let ledgerPage = $state(1);
  const ledgerPageSize = 10;
  let ledgerItems = $state<AdminUserLedgerItem[]>([]);
  let ledgerNextCursor = $state<string | null>(null);
  let ledgerCursors = $state<Array<string | null>>([null]);
  let serverPage = $state(1);
  let serverCursors = $state<Array<string | null>>([null]);
  let serversLoading = $state(false);
  let detailsAbort: AbortController | null = null;
  let userServersAbort: AbortController | null = null;
  let ledgerLoading = $state(false);
  let ledgerError = $state<string | null>(null);
  let serverHistoryError = $state<string | null>(null);
  let ledgerAbort: AbortController | null = null;

  const isAdmin = $derived($user?.role === "admin");
  const visible = $derived(users);
  const isCardView = $derived($route.name === "admin-user" && Boolean($route.args.id));

  async function copyText(text: string, field: string) {
    if (!(await copyToClipboard(text))) {
      error = "Не удалось скопировать значение. Выделите его и скопируйте вручную.";
      return;
    }
    copiedField = field;
    setTimeout(() => {
      if (copiedField === field) {
        copiedField = null;
      }
    }, 1800);
  }

  function handleSearch(event: SubmitEvent) {
    event.preventDefault();
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    if (debouncedQuery !== query) {
      debouncedQuery = query;
    } else {
      void loadUsers();
    }
  }

  function prevPage() {
    if (page > 1 && !loading) {
      void loadUsers(page - 1);
    }
  }
  function nextPage() {
    if (nextCursor && !loading) {
      void loadUsers(page + 1);
    }
  }

  function prevLedgerPage() {
    changeLedgerPage(-1);
  }

  function nextLedgerPage() {
    changeLedgerPage(1);
  }

  async function loadUsers(targetPage = 1) {
    if (!$token) {
      return;
    }
    usersAbort?.abort();
    if (targetPage === 1) {
      nextCursor = null;
      pageCursors = [null];
      page = 1;
      users = [];
    }
    const controller = new AbortController();
    usersAbort = controller;
    loading = true;
    try {
      const result = await api.adminUsers(
        $token,
        debouncedQuery,
        controller.signal,
        targetPage === 1 ? null : targetPage > page ? nextCursor : pageCursors[targetPage - 1],
      );
      if (controller.signal.aborted) {
        return;
      }
      const cursor =
        targetPage === 1 ? null : targetPage > page ? nextCursor : pageCursors[targetPage - 1];
      pageCursors = targetPage === 1 ? [null] : [...pageCursors.slice(0, targetPage - 1), cursor];
      users = result.items;
      nextCursor = result.nextCursor;
      page = targetPage;
      error = null;
    } catch (cause) {
      if ((cause as Error).name === "AbortError" || controller.signal.aborted) {
        return;
      }
      error = cause instanceof ApiError ? cause.message : "Не удалось загрузить пользователей";
    } finally {
      if (!controller.signal.aborted) {
        loading = false;
      }
    }
  }

  async function loadLedger(id: string, p = 1) {
    if (!$token) {
      return;
    }
    ledgerAbort?.abort();
    if (p === 1) {
      ledgerNextCursor = null;
      ledgerCursors = [null];
      ledgerPage = 1;
    }
    const controller = new AbortController();
    ledgerAbort = controller;
    ledgerLoading = true;
    try {
      const res = await api.adminUserLedger(
        $token,
        id,
        p === 1 ? null : p > ledgerPage ? ledgerNextCursor : ledgerCursors[p - 1],
        ledgerPageSize,
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      ledgerItems = res.items;
      const cursor = p === 1 ? null : p > ledgerPage ? ledgerNextCursor : ledgerCursors[p - 1];
      ledgerCursors = p === 1 ? [null] : [...ledgerCursors.slice(0, p - 1), cursor];
      ledgerNextCursor = res.nextCursor;
      ledgerPage = p;
      ledgerError = null;
    } catch (cause) {
      if ((cause as Error).name === "AbortError" || controller.signal.aborted) {
        return;
      }
      ledgerError =
        cause instanceof ApiError ? cause.message : "Не удалось загрузить историю операций";
    } finally {
      if (!controller.signal.aborted) {
        ledgerLoading = false;
      }
    }
  }

  function changeLedgerPage(delta: number) {
    if (!$route.args.id) {
      return;
    }
    const targetPage = ledgerPage + delta;
    if (targetPage < 1 || (delta > 0 && !ledgerNextCursor) || ledgerLoading) {
      return;
    }
    void loadLedger($route.args.id, targetPage);
  }

  async function loadUser(id: string) {
    if (!$token) {
      return;
    }
    detailsAbort?.abort();
    ledgerAbort?.abort();
    userServersAbort?.abort();
    const controller = new AbortController();
    detailsAbort = controller;
    userLoading = true;
    try {
      const result = await api.adminUser($token, id, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      selectedUser = result;
      ledgerPage = 1;
      ledgerCursors = [null];
      ledgerItems = result.ledger;
      ledgerNextCursor = result.ledgerNextCursor;
      ledgerError = null;
      serverHistoryError = null;
      serverPage = 1;
      serverCursors = [null];
      serversLoading = false;
      error = null;
    } catch (cause) {
      if (controller.signal.aborted) {
        return;
      }
      error =
        cause instanceof ApiError ? cause.message : "Не удалось загрузить карточку пользователя";
    } finally {
      if (!controller.signal.aborted) {
        userLoading = false;
      }
    }
  }

  async function changeServerPage(delta: number) {
    if (!$token || !selectedUser || serversLoading) {
      return;
    }
    const targetPage = serverPage + delta;
    const cursor = delta > 0 ? selectedUser.serversNextCursor : serverCursors[targetPage - 1];
    if (targetPage < 1 || (delta > 0 && !cursor)) {
      return;
    }
    userServersAbort?.abort();
    const controller = new AbortController();
    userServersAbort = controller;
    serversLoading = true;
    try {
      const result = await api.adminUserServers(
        $token,
        selectedUser.user.id,
        cursor,
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      serverCursors = [...serverCursors.slice(0, targetPage - 1), cursor];
      serverPage = targetPage;
      serverHistoryError = null;
      selectedUser = {
        ...selectedUser,
        servers: result.items,
        serversNextCursor: result.nextCursor,
      };
    } catch (cause) {
      if (!controller.signal.aborted) {
        serverHistoryError =
          cause instanceof ApiError ? cause.message : "Не удалось загрузить серверы";
      }
    } finally {
      if (!controller.signal.aborted) {
        serversLoading = false;
      }
    }
  }

  async function roleChange(target: AdminUser, role: string) {
    if (!$token || !isAdmin || role === target.role) {
      return;
    }
    if (!(await confirmModal("Изменить роль", `${target.username}: назначить роль ${role}?`))) {
      return;
    }
    busy = true;
    try {
      await api.adminSetRole($token, target.id, role);
      if (isCardView && $route.args.id) {
        await loadUser($route.args.id);
      } else {
        await loadUsers();
      }
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Роль не изменена";
    } finally {
      busy = false;
    }
  }

  async function adjustBalance(target: AdminUser) {
    if (!$token || !isAdmin) {
      return;
    }
    const raw = await promptModal("Изменение баланса", "", "+100 или -100", "Продолжить");
    if (!raw || !/^-?\d+$/.test(raw)) {
      if (raw !== null) {
        error = "Введите целое число";
      }
      return;
    }
    const reason = await promptModal(
      "Причина операции",
      "",
      "Например: компенсация",
      "Списать / начислить",
    );
    if (!reason?.trim()) {
      return;
    }
    busy = true;
    try {
      await api.adminAdjustCredits($token, target.id, Number(raw), reason);
      if (isCardView && $route.args.id) {
        await loadUser($route.args.id);
      } else {
        await loadUsers();
      }
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Баланс не изменён";
    } finally {
      busy = false;
    }
  }

  async function adjustServerLimit(target: AdminUser) {
    if (!$token || !isAdmin) {
      return;
    }
    const raw = await promptModal(
      "Лимит слотов серверов",
      String(target.serverLimit),
      "Число от 1 до 100",
      "Сохранить",
    );
    if (!raw || !/^\d+$/.test(raw)) {
      return;
    }
    const limit = Number(raw);
    if (limit < 1 || limit > 100) {
      error = "Лимит должен быть от 1 до 100";
      return;
    }
    busy = true;
    try {
      await api.adminSetServerLimit($token, target.id, limit);
      if (isCardView && $route.args.id) {
        await loadUser($route.args.id);
      } else {
        await loadUsers();
      }
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Лимит не изменён";
    } finally {
      busy = false;
    }
  }

  async function handleServerAction(server: AdminServer, type: "stop" | "kill" | "delete") {
    if (!$token || busy || (type === "delete" && !isAdmin)) {
      return;
    }
    const label =
      type === "delete"
        ? "Удалить сервер? Операция необратима."
        : type === "kill"
          ? "Аварийно завершить процесс контейнера?"
          : "Остановить сервер?";
    if (
      !(await confirmModal(
        label,
        `${server.name} · владелец ${server.ownerUsername}`,
        type === "delete"
          ? "Удалить"
          : type === "kill"
            ? "Аварийно остановить процесс"
            : "Остановить",
      ))
    ) {
      return;
    }
    busy = true;
    try {
      if (type === "stop") {
        await api.adminStopServer($token, server.id);
      } else if (type === "kill") {
        await api.adminKillServer($token, server.id);
      } else {
        await api.adminDeleteServer($token, server.id);
      }
      if (isCardView && $route.args.id) {
        await loadUser($route.args.id);
      }
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Операция не выполнена";
    } finally {
      busy = false;
    }
  }

  $effect(() => {
    if (isCardView && $route.args.id) {
      void loadUser($route.args.id);
    } else {
      void debouncedQuery;
      void loadUsers();
    }
    return () => {
      detailsAbort?.abort();
      userServersAbort?.abort();
      usersAbort?.abort();
      ledgerAbort?.abort();
    };
  });
</script>

<AdminShell section="users">
  {#if error}
    <div class="form-error" role="alert">
      {error}
      {#if isCardView && !selectedUser}
        <button
          class="ghost-button"
          type="button"
          onclick={() => $route.args.id && void loadUser($route.args.id)}
          disabled={userLoading}>Повторить загрузку</button
        >
      {:else if !isCardView}
        <Button variant="ghost" size="compact" disabled={loading} onclick={() => void loadUsers()}
          >Повторить загрузку</Button
        >
      {/if}
    </div>
  {/if}

  {#if isCardView}
    <!-- КАРТОЧКА ПОЛЬЗОВАТЕЛЯ -->
    <a class="back-link" href={routeHref("admin-users")}>← Все пользователи</a>

    {#if userLoading || (!selectedUser && !error)}
      <LoadingState message="Загрузка карточки пользователя…" />
    {:else if selectedUser}
      {@const u = selectedUser.user}
      <section class="panel-card admin-card user-detail-card">
        <!-- Шапка карточки -->
        <header class="detail-header">
          <div class="user-header-left">
            <div class="user-avatar-circle" aria-hidden="true">
              {u.username.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div class="user-title-row">
                <h2>{u.username}</h2>
                <span class={`user-role-tag role-${u.role}`}>{u.role}</span>
              </div>
              <div class="user-header-chips">
                <button
                  class="meta-chip"
                  type="button"
                  onclick={() => copyText(u.email, "email")}
                  title="Нажмите, чтобы скопировать email"
                >
                  <span>{u.email}</span>
                  <small>{copiedField === "email" ? "скопировано" : "копировать"}</small>
                </button>
                <button
                  class="meta-chip chip-mono"
                  type="button"
                  onclick={() => copyText(u.id, "id")}
                  title="Нажмите, чтобы скопировать ID"
                >
                  <span>ID: {u.id.slice(0, 8)}…</span>
                  <small>{copiedField === "id" ? "скопировано" : "копировать"}</small>
                </button>
              </div>
            </div>
          </div>
        </header>

        <!-- 4 блока информации и управления -->
        <div class="user-overview-grid">
          <!-- Баланс -->
          <article class="user-stat-box">
            <span class="box-label">Баланс кредитов</span>
            <strong class="box-value">{formatNumber(u.balance)} <small>кр.</small></strong>
            <div class="box-action">
              {#if isAdmin}
                <button
                  class="button compact-btn"
                  type="button"
                  onclick={() => void adjustBalance(u)}
                  disabled={busy}
                >
                  Изменить баланс
                </button>
              {/if}
            </div>
          </article>

          <!-- Слоты серверов -->
          <article class="user-stat-box">
            <span class="box-label">Лимит слотов серверов</span>
            <strong class="box-value">
              Лимит: {u.serverLimit} слотов
            </strong>
            <div class="box-action">
              {#if isAdmin}
                <button
                  class="ghost-button compact-btn"
                  type="button"
                  onclick={() => void adjustServerLimit(u)}
                  disabled={busy}
                >
                  Изменить лимит
                </button>
              {/if}
            </div>
          </article>

          <!-- Роль -->
          <article class="user-stat-box">
            <span class="box-label">Роль в системе</span>
            <div class="role-select-wrap">
              <Select
                value={u.role}
                aria-label="Роль в системе"
                onchange={(value) => void roleChange(u, String(value))}
                disabled={!isAdmin || busy}
                options={[
                  { value: "user", label: "user (пользователь)" },
                  { value: "operator", label: "operator (оператор)" },
                  { value: "admin", label: "admin (администратор)" },
                ]}
              />
            </div>
            <span class="box-hint"
              >{isAdmin ? "Изменение применяется сразу" : "Только просмотр"}</span
            >
          </article>

          <!-- Активность -->
          <article class="user-stat-box">
            <span class="box-label">Активность</span>
            <dl class="user-dates-dl">
              <div>
                <dt>Регистрация:</dt>
                <dd>{formatDate(u.createdAt)}</dd>
              </div>
              <div>
                <dt>Последний вход:</dt>
                <dd>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "нет данных"}</dd>
              </div>
              <div>
                <dt>Суточный бонус:</dt>
                <dd>{u.lastDailyBonusAt ? formatDate(u.lastDailyBonusAt) : "не получал"}</dd>
              </div>
            </dl>
          </article>
        </div>

        <!-- Серверы пользователя -->
        <section class="user-sub-section">
          <header class="section-title-row">
            <h3>Серверы пользователя</h3>
          </header>

          {#if serverHistoryError}<p class="form-error" role="alert">{serverHistoryError}</p>{/if}
          {#if selectedUser.servers.length === 0}
            <EmptyState
              title="Нет созданных серверов"
              message="У этого пользователя пока нет игровых серверов."
            />
          {:else}
            <AdminTable
              label="Таблица серверов пользователя. Прокрутите горизонтально для просмотра всех столбцов"
            >
              <thead>
                <tr>
                  <th>Сервер</th>
                  <th>Статус</th>
                  <th>Нода / Порт</th>
                  <th>Ресурсы</th>
                  <th>Обновлён</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {#each selectedUser.servers as s (s.id)}
                  <tr>
                    <td>
                      <a class="table-link" href={routeHref("admin-server", { id: s.id })}>
                        <strong>{s.name}</strong>
                      </a>
                      <small>{s.game} · {s.core} {s.version}</small>
                    </td>
                    <td>
                      <span class={`node-status ${s.status}`}>
                        {statusLabels[s.status] ?? s.status}
                      </span>
                      {#if s.lastError}
                        <small class="table-error">{s.lastError}</small>
                      {/if}
                    </td>
                    <td>
                      {#if s.nodeId}
                        <a class="table-link" href={routeHref("admin-node", { id: s.nodeId })}>
                          {s.nodeName ?? "перейти"}
                        </a>
                      {:else}
                        {s.nodeName ?? "не назначена"}
                      {/if}
                      {#if s.hostPort}<small>:{s.hostPort}</small>{/if}
                    </td>
                    <td>
                      <span title={`ОЗУ: ${s.memLimitMb} МБ, Диск: ${s.diskLimitMb} МБ`}>
                        {s.memLimitMb} МБ / {s.cpuLimitPercent}% CPU
                      </span>
                    </td>
                    <td>{formatDateTime(s.updatedAt)}</td>
                    <td class="table-actions">
                      <a
                        class="ghost-button compact"
                        href={routeHref("admin-server", { id: s.id })}
                      >
                        Открыть
                      </a>
                      {#if ["running", "starting"].includes(s.status)}
                        <button
                          class="ghost-button compact"
                          type="button"
                          onclick={() => void handleServerAction(s, "stop")}
                          disabled={busy}
                        >
                          Стоп
                        </button>
                      {/if}
                      {#if ["running", "starting", "stopping", "queued_stop", "queued_start", "failed"].includes(s.status)}
                        <button
                          class="ghost-button compact danger"
                          type="button"
                          onclick={() => void handleServerAction(s, "kill")}
                          disabled={busy}
                        >
                          Аварийно остановить
                        </button>
                      {/if}
                      {#if isAdmin}
                        <button
                          class="ghost-button compact danger"
                          type="button"
                          onclick={() => void handleServerAction(s, "delete")}
                          disabled={busy}
                        >
                          Удалить
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </AdminTable>
          {/if}
        </section>

        {#if serverPage > 1 || selectedUser.serversNextCursor}
          <div class="pagination">
            <span>Страница серверов {serverPage}</span>
            <Button
              variant="ghost"
              size="compact"
              disabled={serverPage === 1 || serversLoading}
              onclick={() => void changeServerPage(-1)}>Назад</Button
            >
            <Button
              variant="ghost"
              size="compact"
              disabled={!selectedUser.serversNextCursor || serversLoading}
              onclick={() => void changeServerPage(1)}>Далее</Button
            >
          </div>
        {/if}
        <!-- История баланса (Ledger) -->
        <section class="user-sub-section">
          <header class="section-title-row">
            <h3>История операций с балансом</h3>
            {#if ledgerLoading}
              <span class="muted" style="font-size: 0.75rem;">Загрузка…</span>
            {/if}
          </header>

          {#if ledgerError}<p class="form-error" role="alert">{ledgerError}</p>{/if}
          {#if ledgerItems.length === 0 && !ledgerLoading}
            <EmptyState
              title="История пуста"
              message="Операций с кредитами для этого пользователя не зафиксировано."
            />
          {:else}
            <AdminTable
              label="История операций с балансом. Прокрутите горизонтально для просмотра всех столбцов"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Дата и время</th>
                  <th>Операция</th>
                  <th>Сумма</th>
                  <th>Баланс после</th>
                  <th>Детали / Ref</th>
                </tr>
              </thead>
              <tbody>
                {#each ledgerItems as entry (entry.id)}
                  {@const isPositive = entry.amount > 0}
                  <tr>
                    <td class="chip-mono"><small>#{entry.id}</small></td>
                    <td>{formatDateTime(entry.createdAt)}</td>
                    <td>
                      <strong>{ledgerSourceLabels[entry.source] ?? entry.source}</strong>
                    </td>
                    <td>
                      <strong class={isPositive ? "amount-positive" : "amount-negative"}>
                        {isPositive ? `+${entry.amount}` : entry.amount}
                      </strong>
                    </td>
                    <td>{formatNumber(entry.balanceAfter)} кр.</td>
                    <td class="ledger-meta-cell">
                      {#if entry.refId}
                        <span class="ref-text">{entry.refId}</span>
                      {/if}
                      {#if entry.meta && typeof entry.meta === "object"}
                        <small class="meta-json">{JSON.stringify(entry.meta)}</small>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </AdminTable>
          {/if}
          {#if ledgerPage > 1 || ledgerNextCursor}
            <div class="admin-pagination">
              <span>Страница {ledgerPage}</span>
              <div>
                <button
                  class="ghost-button compact"
                  type="button"
                  onclick={prevLedgerPage}
                  disabled={ledgerPage === 1 || ledgerLoading}
                >
                  Назад
                </button>
                <button
                  class="ghost-button compact"
                  type="button"
                  onclick={nextLedgerPage}
                  disabled={!ledgerNextCursor || ledgerLoading}
                >
                  Вперёд
                </button>
              </div>
            </div>
          {/if}
        </section>
      </section>
    {/if}
  {:else}
    <!-- СПИСОК ВСЕХ ПОЛЬЗОВАТЕЛЕЙ -->
    <section class="panel-card admin-card admin-wide-card">
      <div class="admin-toolbar">
        <div>
          <p class="eyebrow">Список аккаунтов</p>
          <h2>Все пользователи <small>{users.length}</small></h2>
        </div>
        <form class="admin-search" onsubmit={handleSearch}>
          <TextInput
            density="compact"
            flexible
            bind:value={query}
            placeholder="Поиск по username или email"
            aria-label="Поиск пользователей"
          />
          <Button type="submit" disabled={loading}>Найти</Button>
        </form>
      </div>

      {#if loading}
        <LoadingState message="Загрузка пользователей…" compact />
      {:else if visible.length === 0}
        <EmptyState title="Ничего не найдено" message="Измените поисковый запрос." />
      {:else}
        <AdminTable
          label="Список пользователей. Прокрутите горизонтально для просмотра всех столбцов"
        >
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Роль</th>
              <th>Баланс</th>
              <th>Серверы</th>
              <th>Последний вход</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {#each visible as target (target.id)}
              <tr>
                <td>
                  <a class="table-link" href={routeHref("admin-user", { id: target.id })}>
                    <strong>{target.username}</strong>
                  </a>
                  <small>{target.email}</small>
                </td>
                <td>
                  <span class={`user-role-tag role-${target.role}`}>{target.role}</span>
                </td>
                <td>
                  <strong>{formatNumber(target.balance)}</strong>
                  <small>кредитов</small>
                </td>
                <td>{target.serverCount} / {target.serverLimit}</td>
                <td>
                  {target.lastLoginAt ? formatDateTime(target.lastLoginAt) : "нет входа"}
                </td>
                <td class="table-actions">
                  <a
                    class="button compact"
                    href={routeHref("admin-user", { id: target.id })}
                    title="Открыть полную карточку пользователя"
                  >
                    Карточка
                  </a>
                </td>
              </tr>
            {/each}
          </tbody>
        </AdminTable>
      {/if}
      <div class="admin-pagination">
        <span>Страница {page}</span>
        <div>
          <button
            class="ghost-button compact"
            type="button"
            onclick={prevPage}
            disabled={page === 1 || loading}
          >
            Назад
          </button>
          <button
            class="ghost-button compact"
            type="button"
            onclick={nextPage}
            disabled={!nextCursor || loading}
          >
            Вперёд
          </button>
        </div>
      </div>
    </section>
  {/if}
</AdminShell>

<style>
  .user-detail-card {
    display: grid;
    gap: 2rem;
    padding: 2rem;
  }

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--line);
  }

  .user-header-left {
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .user-avatar-circle {
    display: grid;
    width: 3.8rem;
    height: 3.8rem;
    flex-shrink: 0;
    place-items: center;
    border: 2px solid rgb(214 75 50 / 0.45);
    border-radius: 50%;
    color: var(--white);
    background: radial-gradient(circle at 35% 30%, var(--mars-light), var(--ink-deep));
    font: 700 1.2rem/1 var(--font-display);
    box-shadow: 0 0 20px rgb(214 75 50 / 0.3);
  }

  .user-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 0.35rem;
  }

  .user-title-row h2 {
    margin: 0;
    color: var(--white);
    font: 600 clamp(1.4rem, 3vw, 1.8rem)/1.1 var(--font-display);
  }

  .user-role-tag {
    display: inline-flex;
    padding: 0.2rem 0.55rem;
    border-radius: 2px;
    font: 600 0.65rem/1 var(--font-display);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .role-admin {
    border: 1px solid rgb(214 75 50 / 0.6);
    color: #fff;
    background: rgb(214 75 50 / 0.28);
  }

  .role-operator {
    border: 1px solid rgb(232 162 77 / 0.6);
    color: var(--amber);
    background: rgb(232 162 77 / 0.18);
  }

  .role-user {
    border: 1px solid var(--line-strong);
    color: var(--cream);
    background: rgb(241 225 198 / 0.08);
  }

  .user-header-chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.55rem;
    border: 1px solid var(--line);
    border-radius: 2px;
    color: var(--muted);
    background: rgb(0 0 0 / 0.25);
    font-size: 0.75rem;
    transition:
      color 140ms ease,
      border-color 140ms ease;
  }

  .meta-chip:hover {
    border-color: var(--line-strong);
    color: var(--cream);
  }

  .meta-chip small {
    color: var(--dim);
    font-size: 0.65rem;
  }

  .chip-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.72rem;
  }

  /* 4 Boxes Grid */
  .user-overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 1rem;
  }

  .user-stat-box {
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
    border: 1px solid var(--line);
    border-radius: 2px;
    background: rgb(241 225 198 / 0.02);
  }

  .box-label {
    margin-bottom: 0.5rem;
    color: var(--dim);
    font: 600 0.68rem/1 var(--font-display);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .box-value {
    color: var(--white);
    font: 600 1.5rem/1.1 var(--font-display);
  }

  .box-value small {
    color: var(--muted);
    font-size: 0.8rem;
    font-weight: 400;
  }

  .box-action {
    margin-top: auto;
    padding-top: 0.8rem;
  }

  .compact-btn {
    width: 100%;
    padding: 0.45rem 0.75rem;
    font-size: 0.72rem;
  }

  .role-select-wrap {
    margin: 0.4rem 0 0.5rem;
  }

  .role-dropdown {
    width: 100%;
    padding: 0.45rem 0.65rem;
    border: 1px solid var(--line-strong);
    border-radius: 2px;
    color: var(--cream);
    background: var(--ink);
    font-size: 0.78rem;
  }

  .box-hint {
    color: var(--dim);
    font-size: 0.68rem;
  }

  .user-dates-dl {
    display: grid;
    gap: 0.45rem;
    margin: 0.35rem 0 0;
    font-size: 0.72rem;
  }

  .user-dates-dl div {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .user-dates-dl dt {
    color: var(--dim);
  }

  .user-dates-dl dd {
    margin: 0;
    color: var(--cream);
    font-weight: 500;
  }

  /* Sub sections */
  .user-sub-section {
    display: grid;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--line);
  }

  .section-title-row h3 {
    margin: 0;
    color: var(--white);
    font: 600 1.1rem/1.2 var(--font-display);
  }

  .amount-positive {
    color: var(--green);
  }

  .amount-negative {
    color: var(--mars-light);
  }

  .ledger-meta-cell {
    max-width: 18rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ref-text {
    display: block;
    color: var(--dim);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.7rem;
  }

  .meta-json {
    display: block;
    color: var(--dim);
    font-size: 0.68rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
